import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import TerminalIcon from '@mui/icons-material/Terminal';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggleButton from '../components/ThemeToggleButton';

export default function LoginPage() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isDark = theme.palette.mode === 'dark';

  React.useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError(t('login.fillAllFields'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || t('login.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const gradient = isDark
    ? 'linear-gradient(135deg, #8b8bff 0%, #5ee7f1 100%)'
    : 'linear-gradient(135deg, #5b5bf6 0%, #22c3d1 100%)';

  /* ============ LEFT HERO PANEL (desktop) ============ */
  const heroPanel = isDesktop && (
    <Box
      sx={{
        position: 'relative',
        flex: '0 0 42%',
        p: 6,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        borderRadius: '20px 0 0 20px',
        background: isDark
          ? 'linear-gradient(160deg, rgba(139,139,255,0.18), rgba(94,231,241,0.08) 60%, rgba(244,114,182,0.06))'
          : 'linear-gradient(160deg, rgba(91,91,246,0.18), rgba(34,195,209,0.10) 60%, rgba(236,72,153,0.08))',
      }}
    >
      {/* Decorative blobs */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          '&::before, &::after': {
            content: '""',
            position: 'absolute',
            borderRadius: '50%',
            filter: 'blur(60px)',
          },
          '&::before': {
            width: 360,
            height: 360,
            top: -120,
            left: -100,
            background: isDark
              ? 'radial-gradient(circle, rgba(139,139,255,.5), transparent 70%)'
              : 'radial-gradient(circle, rgba(91,91,246,.4), transparent 70%)',
          },
          '&::after': {
            width: 400,
            height: 400,
            bottom: -150,
            right: -120,
            background: isDark
              ? 'radial-gradient(circle, rgba(94,231,241,.35), transparent 70%)'
              : 'radial-gradient(circle, rgba(34,195,209,.3), transparent 70%)',
          },
        }}
      />
      {/* Grid pattern */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(var(--grid) 1px, transparent 1px), linear-gradient(90deg, var(--grid) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          maskImage: 'radial-gradient(ellipse at 50% 50%, #000 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 50%, #000 30%, transparent 80%)',
          opacity: 0.7,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 6 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 8,
                borderRadius: 1,
                background: 'var(--surface-solid)',
              },
            }}
          >
            <TerminalIcon sx={{ fontSize: 20, color: 'var(--primary)', position: 'relative', zIndex: 1 }} />
          </Box>
          <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
            {t('login.title')}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          {t('login.heroBadge')}
        </Typography>
        <Typography
          variant="h2"
          sx={{
            mt: 2,
            mb: 3,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            fontSize: '3rem',
          }}
        >
          <Trans
            i18nKey="login.heroTitle"
            components={{
              1: <span style={{ backgroundImage: `linear-gradient(135deg, ${isDark ? '#8b8bff 0%, #5ee7f1 50%, #f472b6 100%' : '#5b5bf6 0%, #22c3d1 50%, #ec4899 100%'})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }} />,
            }}
          />
        </Typography>
        <Typography variant="body1" sx={{ color: 'var(--text-muted)', maxWidth: 400 }}>
          {t('login.heroDesc')}
        </Typography>
      </Box>
    </Box>
  );

  /* ============ RIGHT FORM PANEL ============ */
  const formPanel = (
    <Box
      sx={{
        flex: isDesktop ? '1 1 auto' : 'unset',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2.5, md: 6 },
        minHeight: isDesktop ? '100vh' : '100vh',
        position: 'relative',
      }}
    >
      {/* Top-right theme switch */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 24,
          zIndex: 2,
        }}
      >
        <ThemeToggleButton />
      </Box>

      <Card
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 3,
          border: '1px solid var(--border)',
          boxShadow: isDark ? '0 30px 80px rgba(0,0,0,.5)' : '0 30px 80px rgba(15,23,42,.14)',
          p: { xs: 3.5, md: 5 },
        }}
      >
        {/* Brand mark for mobile */}
        {!isDesktop && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2.5,
                background: gradient,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                mb: 2,
                boxShadow: isDark
                  ? '0 12px 30px -10px rgba(139,139,255,.8)'
                  : '0 12px 30px -10px rgba(91,91,246,.5)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 10,
                  borderRadius: 1.5,
                  background: 'var(--surface-solid)',
                },
              }}
            >
              <TerminalIcon sx={{ fontSize: 28, color: 'var(--primary)', position: 'relative', zIndex: 1 }} />
            </Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em' }}>
              {t('login.title')}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            {isDesktop ? t('login.formTitleDesktop') : t('login.formTitleMobile')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
            {isDesktop
              ? t('login.formSubtitleDesktop')
              : t('login.formSubtitleMobile')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t('login.username')}
            placeholder={t('login.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            autoFocus
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label={t('login.password')}
            type={showPassword ? 'text' : 'password'}
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 3, py: 1.5, borderRadius: 1.5, fontSize: '1rem' }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('login.loginButton')}
          </Button>
        </form>

        <Typography
          variant="caption"
          align="center"
          sx={{ color: 'var(--text-subtle)', display: 'block', mt: 4 }}
        >
          {t('login.copyright', { year: new Date().getFullYear() })} · {t('login.footer')}
        </Typography>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', position: 'relative' }}>
      {/* Background mesh */}
      <Box className="app-bg" aria-hidden="true" />
      {heroPanel}
      {formPanel}
    </Box>
  );
}